import { Lock, Zap, LayoutDashboard, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useToolAccess } from "@/hooks/useToolAccess";

interface LockedToolOverlayProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  /** Tool identifier for database-driven access control */
  toolId: string;
}

/**
 * Wraps a tool card and shows a lock overlay for restricted users.
 * Access is determined by:
 * 1. Database overrides (plan_credit_overrides table)
 * 2. Membership status (paid vs free)
 * 3. Credit balance for free users
 */
export function LockedToolOverlay({ 
  children, 
  onClick, 
  className = "",
  toolId
}: LockedToolOverlayProps) {
  const navigate = useNavigate();
  const { isToolLocked, isLoading } = useToolAccess();

  const locked = isToolLocked(toolId);

  // Show tools normally for users with access or while loading
  if (isLoading || !locked) {
    return (
      <div onClick={onClick} className={className}>
        {children}
      </div>
    );
  }

  // Users without access see a lock overlay on the button area only
  return (
    <div className={`relative ${className}`}>
      {/* The tool card - fully visible so user can read title/description */}
      <div className="pointer-events-none select-none">
        {children}
      </div>
      
      {/* Lock overlay covering only the bottom button area */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/80 via-black/60 to-transparent rounded-b-xl flex flex-col items-center justify-end pb-4 cursor-pointer transition-all duration-300 group"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Compact lock badge */}
        <div className="flex items-center gap-3 bg-black/70 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 group-hover:scale-105 transition-transform duration-300">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-yellow-400" />
            <span className="text-white font-medium text-sm">Upgrade to Unlock</span>
          </div>
          <Button 
            size="sm"
            className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-blue-900 font-bold px-3 py-1 text-xs shadow-lg"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/pricing');
            }}
          >
            <Zap className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
        </div>
      </div>
    </div>
  );
}
